// src/AppRoutes.js
import DevSeoGuard from "./components/DevSeoGuard";
import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
  useParams,
  Outlet,
} from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import GlobalModalScrollLock from "./components/GlobalModalScrollLock";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { initAnalytics, trackPageView } from "./lib/analytics";
import { hasAnalyticsConsent } from "./lib/consent";
import { eventTypesBySlug } from "./data/eventTypes";

// Shared UI
import Header from "./components/Header";
import Footer from "./components/Footer";

import CancellationRefunds from "./pages/CancellationRefunds";

// Lazy pages
const Home = lazy(() => import("./App"));
const ChatWidget = lazy(() => import("./components/ChatWidget"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CityPage = lazy(() => import("./pages/CityPage"));
const NorthernIrelandHub = lazy(() => import("./pages/NorthernIrelandHub"));
const RecentSetupsHub = lazy(() => import("./pages/RecentSetupsHub"));
const RecentSetupPage = lazy(() => import("./pages/RecentSetupPage"));

// Services
const ServicesHub = lazy(() => import("./pages/ServiceHub"));
const ServiceRoute = lazy(() => import("./features/services/ServiceRoute"));

// Event-type landing pages
const EventTypePage = lazy(() => import("./pages/EventTypePage"));

// Packages
const PackagesHub = lazy(() => import("./pages/PackagesHub"));
const PackageRoute = lazy(() => import("./features/packages/PackageRoute"));

// Smooth scroll manager with fixed-header offset + retries
function ScrollManager() {
  const location = useLocation();

  useEffect(() => {
    const getHeaderOffset = () => {
      const header = document.querySelector("header");
      const h = header?.getBoundingClientRect?.().height || 64; // fallback
      return Math.ceil(h) + 8; // a little breathing room
    };

    // no hash → jump to top
    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      return;
    }

    const id = location.hash.slice(1);
    let raf = 0;
    let tries = 0;

    const tick = () => {
      const el = document.getElementById(id);
      if (el) {
        const top =
          el.getBoundingClientRect().top + window.scrollY - getHeaderOffset();
        window.scrollTo({ top, behavior: "smooth" });
      } else if (tries < 15) {
        tries += 1;
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [location]);

  return null;
}

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    if (!hasAnalyticsConsent()) return;
    initAnalytics();
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
}

// Minimal 404
function NotFound() {
  return (
    <main className="mx-auto max-w-6xl px-4 py-16">
      <Helmet>
        <title>Page not found | Party Crafters</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <h1 className="text-3xl font-semibold mb-4">Page not found</h1>
      <p className="mb-6 text-gray-700">
        We couldn't find that page. Try one of these:
      </p>
      <div className="flex gap-3 flex-wrap">
        <Link
          to="/"
          className="inline-block px-6 py-3 rounded-full bg-pink-500 text-white font-semibold"
        >
          Go to Homepage
        </Link>
      </div>
    </main>
  );
}

// Checks the slug against known event-type pages before rendering.
// Unknown single-segment slugs fall through to NotFound.
function EventTypeOrNotFound() {
  const { eventSlug } = useParams();
  const slug = (eventSlug || "").toLowerCase();
  if (!eventTypesBySlug[slug]) return <NotFound />;
  return <EventTypePage />;
}

function RecentSetupRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/gallery/${slug}`} replace />;
}

// Layout keeps Header/Footer inside the Router
function Layout() {
  const [showChatWidget, setShowChatWidget] = useState(false);

  useEffect(() => {
    const loadChatWidget = () => setShowChatWidget(true);
    let idleId;
    let timeoutId;

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(loadChatWidget, { timeout: 3000 });
    } else {
      timeoutId = window.setTimeout(loadChatWidget, 2500);
    }

    return () => {
      if (idleId) window.cancelIdleCallback(idleId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <>
      <GlobalModalScrollLock />
      <Header />
      {/* pad for fixed header; adjust if you change header height */}
      <div className="pt-14">
        <Suspense fallback={<main className="mx-auto max-w-6xl px-4 py-16 text-center">Loading…</main>}>
          <Outlet />
        </Suspense>
      </div>
      <Footer />
      {showChatWidget && (
        <Suspense fallback={null}>
          <ChatWidget />
        </Suspense>
      )}
      <CookieConsentBanner />
    </>
  );
}

export default function AppRoutes() {
  return (
    <BrowserRouter>
      {/* Global default title template (optional, nice for SEO hygiene) */}
      <Helmet defaultTitle="Party Crafters" titleTemplate="%s | Party Crafters" />
      <ScrollManager />
      <AnalyticsTracker />
      {(((typeof import.meta !== "undefined" && import.meta.env?.MODE === "development") ||
        process.env.NODE_ENV === "development") && <DevSeoGuard />)}
      <Routes>
        <Route element={<Layout />}>
          {/* Home */}
          <Route path="/" element={<Home />} />

          {/* Static pages */}
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy.html" element={<PrivacyPolicy />} />
          <Route path="/gallery" element={<RecentSetupsHub />} />
          <Route path="/gallery/:slug" element={<RecentSetupPage />} />
          <Route path="/recent-setups" element={<Navigate to="/gallery" replace />} />
          <Route path="/recent-setups/:slug" element={<RecentSetupRedirect />} />

          {/* Services hub + dynamic service page */}
          <Route path="/services" element={<ServicesHub />} />
          <Route path="/services/:slug" element={<ServiceRoute />} />

          {/* Packages hub + dynamic package page */}
          <Route path="/packages" element={<PackagesHub />} />
          <Route path="/packages/:slug" element={<PackageRoute />} />

          {/* Regional + Town hubs */}
          <Route
            path="/northern-ireland/party-decorations"
            element={<NorthernIrelandHub />}
          />
          <Route path="/:town/party-decorations" element={<CityPage />} />

          {/* Cancellation */}
          <Route path="/cancellation-and-refunds" element={<CancellationRefunds />} />

          {/* Event-type landing pages (e.g. /birthday-party-decorators-belfast).
              Unknown single-segment slugs render NotFound. */}
          <Route path="/:eventSlug" element={<EventTypeOrNotFound />} />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
